/* ════════════════════════════════════════════════════════════
   Hero Products conveyor — ACF block `acf/section-hero-products`.
   Port of the design prototype (docs/design_handoff_hero_products_slider/
   reference/prototype.js) into a per-instance, GSAP-driven module.

   · products ride a conveyor: desktop = curved arc, mobile = slide.
   · while a product rests it bobs (CSS) and ingredients drift (CSS).
   · ingredient explosion: each sprite flies in/out from its own random
     direction; hero ingredients (ACF) are larger than the hardcoded
     oat/leaf/pea basics.
   · background blobs morph (CSS) and recolour / pulse / roam (GSAP).
   · per-product NOVO badge fades in/out.
   ════════════════════════════════════════════════════════════ */

import gsap from "gsap";

interface ProductData {
    img: string;
    alt: string;
    name: string;
    category: string;
    color: string;
    new: boolean;
    heroes: string[];
}

interface Sprite {
    url: string;
    w: number;
}

const ANIM = 1150;            // ms — conveyor sweep
const ROTATE_DEFAULT = 4400;  // ms — rest before the belt advances
const SLOTS = 13;             // ingredient sprite pool
const HERO_W = 140;           // px — hero ingredient width
const MOBILE_BP = 900;

const BASIC_W: Record<string, number> = {
    oat1: 17, oat2: 21, leaf1: 35, leaf2: 39, pea1: 24, pea2: 22,
};
const BASIC_POOL = ['oat1', 'leaf1', 'pea1', 'oat2', 'leaf2', 'oat1', 'pea2', 'oat2', 'leaf1'];

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const isMobile = () => window.innerWidth < MOBILE_BP;
const pxv = (v: number) => v.toFixed(1) + 'px';

function shuffle<T>(a: T[]): T[] {
    a = a.slice();
    for (let i = a.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function initStage(stage: HTMLElement): void {
    let products: ProductData[] = [];
    let basics: Record<string, string> = {};
    try {
        products = JSON.parse(stage.dataset.products || '[]');
        basics = JSON.parse(stage.dataset.basics || '{}');
    } catch (e) {
        return;
    }
    if (!products.length) return;

    const single = stage.dataset.single === '1' || products.length <= 1;
    const rotate = parseInt(stage.dataset.rotation || '', 10) || ROTATE_DEFAULT;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── DOM refs (scoped to this stage) ── */
    const blobsLayer = stage.querySelector<HTMLElement>('.hero-products__blobs');
    const halos = Array.from(stage.querySelectorAll<HTMLElement>('.blob'));
    const ingLayer = stage.querySelector<HTMLElement>('.hero-products__ingredients');
    const novo = stage.querySelector<HTMLElement>('.hero-products__novo');
    const nameEl = stage.querySelector<HTMLElement>('.hero-products__name');
    const flavorEl = stage.querySelector<HTMLElement>('.hero-products__name .flavor');
    const catEl = stage.querySelector<HTMLElement>('.hero-products__name .cat');
    const dotsBox = stage.querySelector<HTMLElement>('.hero-products__dots');

    const frontEl = stage.querySelector<HTMLElement>('.hero-products__slide.is-front');
    const backEl = stage.querySelector<HTMLElement>('.hero-products__slide.is-back');
    if (!frontEl || !backEl) return;

    type Slot = { el: HTMLElement; img: HTMLImageElement };
    let front: Slot = { el: frontEl, img: frontEl.querySelector('img') as HTMLImageElement };
    let back: Slot = { el: backEl, img: backEl.querySelector('img') as HTMLImageElement };

    /* preload product + hero ingredient images */
    const preload = new Set<string>();
    products.forEach(p => { if (p.img) preload.add(p.img); (p.heroes || []).forEach(h => preload.add(h)); });
    Object.values(basics).forEach(b => preload.add(b));
    preload.forEach(src => { const im = new Image(); im.src = src; });

    /* ════════════════ BACKGROUND BLOBS ════════════════ */
    // b-main sits behind the product; the two small blobs orbit around it and
    // advance their angle on each product switch for a dynamic, random look.
    const orbitAngle = [0, -Math.PI / 4, Math.PI * 0.78]; // [main(unused), b-2, b-3]

    if (halos.length) gsap.set(halos, { xPercent: -50, yPercent: -50 });

    function paintHalos(color: string): void {
        if (!halos.length) return;
        gsap.to(halos, { backgroundColor: color, duration: 1, ease: 'power1.inOut', overwrite: 'auto' });
    }

    function placeBlob(i: number, x: number, y: number, rot: number, sc: number, animate: boolean): void {
        const b = halos[i];
        if (!b) return;
        const vars = { left: x + '%', top: y + '%', rotation: rot, scale: sc };
        if (animate && !reduceMotion) gsap.to(b, { ...vars, duration: 0.9, ease: 'power2.inOut', overwrite: 'auto' });
        else gsap.set(b, vars);
    }

    const blobCentre = () => (isMobile() ? { cx: 52, cy: 46, rx: 20, ry: 20 } : { cx: 58, cy: 50, rx: 26, ry: 28 });

    // Init: don't move the blobs — only centre them (matching the CSS transform) and
    // seed each small blob's orbit angle from where CSS already painted it, so the
    // first paint → JS hand-off is seamless (no jump). Orbit kicks in on the 1st switch.
    function initBlobs(): void {
        if (!halos.length) return;
        gsap.set(halos, { xPercent: -50, yPercent: -50, rotation: 0, scale: 1 });
        const { cx, cy, rx, ry } = blobCentre();
        const w = (blobsLayer ? blobsLayer.clientWidth : stage.clientWidth) || 1;
        const h = (blobsLayer ? blobsLayer.clientHeight : stage.clientHeight) || 1;
        for (let i = 1; i < halos.length; i++) {
            const cs = getComputedStyle(halos[i]);
            const xPct = (parseFloat(cs.left) || 0) / w * 100;
            const yPct = (parseFloat(cs.top) || 0) / h * 100;
            orbitAngle[i] = Math.atan2((yPct - cy) / ry, (xPct - cx) / rx);
        }
    }

    function positionBlobs(animate: boolean): void {
        if (!halos.length) return;
        const { cx, cy, rx, ry } = blobCentre();
        // b-main: gentle roam around its anchor
        placeBlob(0, cx + rand(-3, 3), cy + rand(-2, 2), rand(-18, 18), rand(.92, 1.08), animate);
        // small blobs: advance their orbit angle, then sit on the circle around b-main
        for (let i = 1; i < halos.length; i++) {
            orbitAngle[i] += (Math.random() < .5 ? -1 : 1) * rand(0.6, 1.5);
            const x = cx + rx * Math.cos(orbitAngle[i]) + rand(-2, 2);
            const y = cy + ry * Math.sin(orbitAngle[i]) + rand(-2, 2);
            placeBlob(i, x, y, rand(-30, 30), rand(.85, 1.12), animate);
        }
    }

    function popBlobs(): void {
        if (reduceMotion || !blobsLayer) return;
        blobsLayer.classList.remove('pop');
        void blobsLayer.offsetWidth;
        blobsLayer.classList.add('pop');
    }

    /* ════════════════ INGREDIENT EXPLOSION ════════════════ */
    const slots: { ing: HTMLElement; drift: HTMLElement; shape: HTMLElement }[] = [];
    if (ingLayer) {
        for (let s = 0; s < SLOTS; s++) {
            const ing = document.createElement('div'); ing.className = 'ing';
            const drift = document.createElement('div'); drift.className = 'ing-drift';
            const shape = document.createElement('div'); shape.className = 'ing-shape';
            drift.appendChild(shape); ing.appendChild(drift); ingLayer.appendChild(ing);
            slots.push({ ing, drift, shape });
        }
    }
    let curActive = 0;

    // The sprite set is rebuilt per product from THAT product's hero ingredients
    // (ACF, mapped to the slide) topped up with the basics. On a switch the old
    // sprites burst out and the incoming product's set bursts in.
    function buildSetFor(p: ProductData): Sprite[] {
        const list: Sprite[] = [];
        const heroUrls: string[] = [];
        (p.heroes || []).forEach(h => { if (h && heroUrls.indexOf(h) === -1) heroUrls.push(h); });
        shuffle(heroUrls).slice(0, Math.min(5, heroUrls.length)).forEach(u => list.push({ url: u, w: HERO_W }));
        const pool = shuffle(BASIC_POOL).filter(k => basics[k]);
        let bi = 0;
        while (list.length < SLOTS && pool.length) {
            const k = pool[bi++ % pool.length];
            list.push({ url: basics[k], w: BASIC_W[k] || 22 });
        }
        return shuffle(list).slice(0, SLOTS);
    }
    let curSet: Sprite[] = [];

    // Even spread via a jittered grid (one sprite per cell) — avoids the dense
    // clumps that pure-random positions produce.
    function genRest(): [number, number][] {
        const mob = isMobile();
        // Near-square grid with one sprite per cell, spanning the full stage so the
        // sprites scatter across the whole area instead of clumping in one region.
        const cols = mob ? 5 : 5;
        const rows = Math.ceil(SLOTS / cols);
        const x0 = mob ? 1 : 1, x1 = mob ? 99 : 99;
        const y0 = mob ? 3 : 2, y1 = mob ? 70 : 97;
        const cw = (x1 - x0) / cols, ch = (y1 - y0) / rows;
        const cells: [number, number][] = [];
        for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) cells.push([c, r]);
        // Keep sprites clear of the central blob (where the product sits): any point
        // landing inside the blob ellipse is pushed radially out onto an edge ring.
        const { cx, cy, rx, ry } = blobCentre();
        const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
        // Jitter nearly fills each cell, so sprites reach cell edges and read as an
        // even, organic scatter rather than a visible lattice.
        return shuffle(cells).slice(0, SLOTS).map(([c, r]) => {
            let x = x0 + (c + rand(0.05, 0.95)) * cw;
            let y = y0 + (r + rand(0.05, 0.95)) * ch;
            const nx = (x - cx) / rx, ny = (y - cy) / ry;
            const nr = Math.hypot(nx, ny);
            if (nr < 1.05) {
                let ux = nx / nr, uy = ny / nr;
                if (!isFinite(ux) || !isFinite(uy) || nr < 1e-3) {
                    const a = rand(0, Math.PI * 2); ux = Math.cos(a); uy = Math.sin(a);
                }
                const tr = rand(1.05, 1.4);  // edge ring just outside the blob
                x = cx + ux * tr * rx;
                y = cy + uy * tr * ry;
            }
            return [clamp(x, x0, x1), clamp(y, y0, y1)] as [number, number];
        });
    }
    function launchVectors(rect: DOMRect): [number, number][] {
        const W = rect.width, H = rect.height;
        return slots.map(() => {
            const a = rand(0, Math.PI * 2), d = rand(.26, .48);
            return [Math.cos(a) * d * W, Math.sin(a) * d * H] as [number, number];
        });
    }
    function applyDrift(sl: { drift: HTMLElement }): void {
        sl.drift.style.setProperty('--dx', rand(-8, 8).toFixed(0) + 'px');
        sl.drift.style.setProperty('--dy', (-4 - rand(0, 9)).toFixed(0) + 'px');
        sl.drift.style.setProperty('--dr', rand(-8, 8).toFixed(0) + 'deg');
        sl.drift.style.setProperty('--ddur', (6.5 + rand(0, 4)).toFixed(1) + 's');
    }

    function enterIngredients(set: Sprite[]): void {
        if (!ingLayer || !slots.length) return;
        curSet = set;
        const rect = ingLayer.getBoundingClientRect();
        const rest = genRest();
        const vecs = launchVectors(rect);
        curActive = SLOTS;
        slots.forEach((sl, i) => {
            const spec = curSet[i];
            if (!spec) { sl.ing.style.display = 'none'; return; }
            sl.ing.style.display = '';
            sl.shape.innerHTML = `<img src="${spec.url}" width="${spec.w}" alt="" loading="eager">`;
            sl.ing.style.left = rest[i][0] + '%';
            sl.ing.style.top = rest[i][1] + '%';
            applyDrift(sl);
            if (reduceMotion) {
                // place at rest, no burst animation
                sl.ing.classList.remove('out', 'burst');
                sl.ing.style.opacity = '1';
                return;
            }
            sl.ing.style.opacity = '';
            sl.ing.style.setProperty('--fx', pxv(vecs[i][0]));
            sl.ing.style.setProperty('--fy', pxv(vecs[i][1]));
            sl.ing.style.setProperty('--r0', rand(-90, 90).toFixed(0) + 'deg');
            sl.ing.style.setProperty('--r1', rand(-45, 45).toFixed(0) + 'deg');
            sl.ing.style.setProperty('--delay', (i * rand(.02, .045) + rand(0, .05)).toFixed(3) + 's');
            sl.ing.classList.remove('out', 'burst');
            void sl.ing.offsetWidth;
            sl.ing.classList.add('burst');
        });
    }
    // On a product switch the current sprites burst out, then the incoming
    // product's set bursts in — so the ingredients always match the slide.
    function exitIngredients(): void {
        if (!ingLayer) return;
        const rect = ingLayer.getBoundingClientRect();
        const vecs = launchVectors(rect);  // same outward vectors as the burst-in
        slots.forEach((sl, i) => {
            if (sl.ing.style.display === 'none') return;
            sl.ing.style.setProperty('--ox', pxv(vecs[i][0]));
            sl.ing.style.setProperty('--oy', pxv(vecs[i][1]));
            sl.ing.style.setProperty('--or', rand(-60, 60).toFixed(0) + 'deg');
            sl.ing.style.setProperty('--odelay', (i * rand(.01, .03)).toFixed(3) + 's');
            sl.ing.classList.remove('burst');
            void sl.ing.offsetWidth;
            sl.ing.classList.add('out');
        });
    }
    function changeIngredients(p: ProductData): void {
        const set = buildSetFor(p);
        if (!curActive || reduceMotion) { enterIngredients(set); return; }
        exitIngredients();
        window.setTimeout(() => enterIngredients(set), Math.round(ANIM * 0.4));
    }

    /* ════════════════ CONVEYOR ════════════════ */
    type Arc = { enter: number; rest: number; exit: number; off: (th: number) => [number, number]; W: number };
    let ARC: Arc;
    function computeArc(): void {
        const r = stage.getBoundingClientRect();
        const W = r.width || 480, H = r.height || 560;
        const Cx = W * 0.92;
        const Cy = -H * 1.30;
        const R = Math.hypot(Cx, Cy);
        const rest = Math.atan2(-Cy, -Cx);
        const enter = rest + 0.56;
        const exit = rest - 0.56;
        const cR = Math.cos(rest), sR = Math.sin(rest);
        const off = (th: number): [number, number] => [R * (Math.cos(th) - cR), R * (Math.sin(th) - sR)];
        ARC = { enter, rest, exit, off, W };
    }
    computeArc();

    type Frame = { x: number; y: number; sc: number; rot: number; op: number };
    function frame(phase: 'enter' | 'exit', t: number): Frame {
        const mob = isMobile();
        const e = easeInOut(t);
        if (phase === 'enter') {
            const op = Math.min(1, t / 0.40);
            if (mob) return { x: lerp(-ARC.W * 0.6, 0, e), y: 0, sc: lerp(.86, 1, e), rot: lerp(-3, 0, e), op };
            const o = ARC.off(lerp(ARC.enter, ARC.rest, e));
            return { x: o[0], y: o[1], sc: lerp(.52, 1, e), rot: lerp(-9, 0, e), op };
        } else {
            const op = t < 0.6 ? 1 : Math.max(0, 1 - (t - 0.6) / 0.4);
            if (mob) return { x: lerp(0, ARC.W * 0.6, e), y: 0, sc: lerp(1, .84, e), rot: lerp(0, 4, e), op };
            const o = ARC.off(lerp(ARC.rest, ARC.exit, e));
            return { x: o[0], y: o[1], sc: lerp(1, .52, e), rot: lerp(0, 9, e), op };
        }
    }
    function applyFrame(slot: Slot, f: Frame): void {
        slot.el.style.transform =
            `translate(-50%,-50%) translate(${f.x.toFixed(1)}px,${f.y.toFixed(1)}px) rotate(${f.rot.toFixed(2)}deg) scale(${f.sc.toFixed(3)})`;
        slot.el.style.opacity = f.op.toFixed(3);
    }
    const REST: Frame = { x: 0, y: 0, sc: 1, rot: 0, op: 1 };

    let switchTween: gsap.core.Tween | null = null;
    let animating = false;

    function finalizeAnim(): void {
        if (switchTween) { switchTween.kill(); switchTween = null; }
        applyFrame(back, REST);
        front.el.style.opacity = '0';
        [front, back] = [back, front];
        animating = false;
    }
    function startSwitch(item: ProductData): void {
        if (animating) finalizeAnim();
        // Incoming slide always above outgoing so it can never sit behind it on overflow.
        back.el.style.zIndex = '11';
        front.el.style.zIndex = '10';
        back.img.src = item.img;
        back.img.alt = item.alt || ('Niamito ' + item.name);
        if (reduceMotion) {
            applyFrame(back, REST);
            front.el.style.opacity = '0';
            [front, back] = [back, front];
            return;
        }
        applyFrame(back, frame('enter', 0));
        animating = true;
        const state = { t: 0 };
        switchTween = gsap.to(state, {
            t: 1,
            duration: ANIM / 1000,
            ease: 'none',
            onUpdate: () => { applyFrame(back, frame('enter', state.t)); applyFrame(front, frame('exit', state.t)); },
            onComplete: finalizeAnim,
        });
    }

    /* ════════════════ NOVO BADGE ════════════════ */
    let novoVisible = false;
    if (novo) gsap.set(novo, { rotation: -12, transformOrigin: '50% 50%' });
    function setNovo(on: boolean): void {
        if (!novo || on === novoVisible) return;
        novoVisible = on;
        gsap.to(novo, {
            autoAlpha: on ? 1 : 0,
            scale: on ? 1 : 0.5,
            duration: 0.46,
            ease: on ? 'power2.out' : 'power1.in',
            overwrite: 'auto',
        });
    }

    /* ════════════════ DOTS + ROTATION ════════════════ */
    const dots: HTMLButtonElement[] = [];
    if (dotsBox && !single) {
        products.forEach((_, i) => {
            const d = document.createElement('button');
            d.type = 'button';
            d.className = 'hp-dot' + (i === 0 ? ' active' : '');
            d.setAttribute('aria-label', 'Show product ' + (i + 1));
            d.addEventListener('click', () => { show(i); restart(); });
            dotsBox.appendChild(d);
            dots.push(d);
        });
    }

    // Update the name pill, hiding it entirely when the product has no name/category.
    function applyName(it: ProductData): void {
        if (!nameEl) return;
        const has = !!((it.name && it.name.trim()) || (it.category && it.category.trim()));
        if (flavorEl) flavorEl.textContent = it.name || '';
        if (catEl) catEl.textContent = it.category || '';
        nameEl.classList.toggle('is-empty', !has);
    }
    applyName(products[0]);

    let idx = 0;
    let timer: number | null = null;

    function show(i: number): void {
        if (i === idx) return;
        idx = i;
        const it = products[i];
        startSwitch(it);
        stage.style.setProperty('--flavor', it.color);
        paintHalos(it.color);
        popBlobs();
        positionBlobs(true);
        changeIngredients(it);
        if (nameEl) {
            nameEl.style.opacity = '0';
            window.setTimeout(() => { applyName(it); nameEl.style.opacity = '1'; }, 260);
        }
        setNovo(it.new);
        dots.forEach((d, k) => d.classList.toggle('active', k === i));
    }
    function next(): void { show((idx + 1) % products.length); }
    function start(): void {
        if (reduceMotion || single) return;
        stop();
        timer = window.setInterval(next, rotate);
    }
    function stop(): void { if (timer) { clearInterval(timer); timer = null; } }
    function restart(): void { stop(); start(); }

    /* ════════════════ INIT ════════════════ */
    const first = products[0];
    front.img.src = first.img;
    front.img.alt = first.alt || ('Niamito ' + first.name);
    applyFrame(front, REST);
    back.el.style.opacity = '0';
    stage.style.setProperty('--flavor', first.color);
    paintHalos(first.color);
    initBlobs();
    if (novo) {
        novoVisible = first.new;
        gsap.set(novo, { autoAlpha: first.new ? 1 : 0, scale: first.new ? 1 : 0.5 });
    }

    // First ingredient entrance + autoplay start once the stage scrolls into view.
    let entered = false;
    const kickoff = () => {
        if (entered) return;
        entered = true;
        computeArc();
        enterIngredients(buildSetFor(first));
        start();
    };
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
            entries.forEach(en => {
                if (en.isIntersecting) { kickoff(); obs.disconnect(); }
            });
        }, { rootMargin: '0px 0px -10% 0px' });
        io.observe(stage);
    } else {
        kickoff();
    }

    window.addEventListener('resize', () => {
        computeArc();
        if (!animating) applyFrame(front, REST);
    });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else if (entered) start();
    });
}

window.addEventListener('load', () => {
    document.querySelectorAll<HTMLElement>('.hero-products__stage').forEach(stage => {
        try {
            initStage(stage);
        } catch (e) {
            console.warn('Hero products init failed:', e);
        }
    });
});
